import React, { cloneElement, forwardRef, isValidElement, useEffect, useState } from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TargetedEvent,
  TouchableHighlight,
  TouchableHighlightProps,
  View,
  ViewStyle,
} from 'react-native';

import { helpers } from '@helpers';

import { FocusableHighlightBottom } from '../FocusableHighlightBottom/FocusableHighlightBottom';
import { ExtendedGestureResponderEvent } from '../TWCButton/types';

export const FocusableHighlight = forwardRef<TouchableHighlight, FocusableHighlightProps>(
  (
    {
      accessibilityHint,
      accessibilityLabel,
      disabled,
      accessibilityRole,
      onBlur,
      onFocus,
      onPress,
      children,
      ...properties
    },
    reference,
  ) => {
    const [focused, setFocused] = useState(false);

    const childrenWithFocused = isValidElement<ClonedElement>(children)
      ? cloneElement<ClonedElement>(children, { focused })
      : null;

    useEffect(() => {
      if (disabled === true) {
        setFocused(false);
      }
    }, [disabled]);

    const onBlurHandler = (event: NativeSyntheticEvent<TargetedEvent>) => {
      setFocused(false);
      onBlur?.(event);
    };

    const onFocusHandler = (event: NativeSyntheticEvent<TargetedEvent>) => {
      setFocused(true);
      onFocus?.(event);
    };

    const onPressHandler = (event: ExtendedGestureResponderEvent) => {
      // Raw press event (no event type)- only fired once
      // Other press events are for keydown and keyup, may or may not be fired depending on platform

      // Fix to disable false events that are triggered before expected natural event on android devices
      if (helpers.isAndroid && event?.eventType === 'select') {
        return;
      }

      const eventType = event.type;
      const tvosPress = Platform.isTV && eventType === 'select';

      if (!eventType || tvosPress) {
        onPress?.(event);
      }
    };

    return (
      <TouchableHighlight
        disabled={disabled}
        {...properties}
        {...{
          accessibilityHint,
          accessibilityLabel,
        }}
        accessibilityRole={accessibilityRole || 'button'}
        accessibilityState={{ disabled }}
        accessible={!disabled}
        ref={reference}
        // if testID is present, set global context ref
        style={[
          properties.style,
          focused && {
            backgroundColor: properties.underlayColor,
            opacity: properties.activeOpacity,
          },
          focused && properties.styleFocused,
        ]}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        onPress={onPressHandler}
      >
        {properties.bottomHighlight ? (
          <FocusableHighlightBottom isFocused={focused} node={children} />
        ) : (
          childrenWithFocused || <View />
        )}
      </TouchableHighlight>
    );
  },
);

export type FocusableHighlightProps = {
  nativeID?: string; // NOT EXIST IN COMPONENT
  bottomHighlight?: boolean;
  styleFocused?: StyleProp<ViewStyle>;
  hasTVPreferredFocus?: boolean;
  nextFocusDown?: number | null;
  nextFocusLeft?: number | null;
  nextFocusRight?: number | null;
  nextFocusUp?: number | null;
} & TouchableHighlightProps;

type ClonedElement = {
  focused: boolean;
};
